import React, { useState } from 'react';

import { Plus } from 'lucide-react';
import { useQuery } from 'react-query';
import { DotBricksLoading } from 'src/common/components';

import { fetchAccountCohorts } from '../api';
import { ACCOUNT_COHORT_MODE_LABEL, ACCOUNT_COHORTS_QUERY_KEY } from '../constants';

import AccountCohortForm from './AccountCohortForm';

const NEW_COHORT = 'new';

const AccountCohortsPanel: React.FC = () => {
  const [selectedId, setSelectedId] = useState<number | typeof NEW_COHORT | null>(null);

  const { data, isLoading } = useQuery([ACCOUNT_COHORTS_QUERY_KEY], fetchAccountCohorts);

  const cohorts = data?.data.results ?? [];
  const selected = selectedId === NEW_COHORT ? null : cohorts.find((c) => c.id === selectedId);

  if (isLoading) return <DotBricksLoading />;

  return (
    <div className="flex h-full overflow-hidden">
      <div className="w-80 flex flex-col flex-shrink-0 overflow-hidden bg-white border-r">
        <div className="flex-shrink-0 p-4 border-b">
          <button
            type="button"
            className="btn btn-primary inline-flex items-center justify-center w-full gap-1 px-4 py-2 text-sm"
            onClick={() => setSelectedId(NEW_COHORT)}
          >
            <Plus size={14} />
            Nuevo cohort de cuentas
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {cohorts.map((cohort) => (
            <button
              key={cohort.id}
              type="button"
              className={`w-full px-4 py-3 text-left border-b border-gray-100 hover:bg-gray-50 ${
                selectedId === cohort.id ? 'bg-blue-50' : ''
              }`}
              onClick={() => setSelectedId(cohort.id)}
            >
              <div className="flex items-center gap-2">
                <span className="flex-1 text-sm font-semibold text-gray-800 truncate">
                  {cohort.name}
                </span>
                {cohort.is_protected && (
                  <span className="px-2 py-0.5 text-xs font-semibold text-blue-700 bg-blue-50 rounded-full">
                    Protegido
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-xs text-gray-500">
                {ACCOUNT_COHORT_MODE_LABEL[cohort.mode]} · {cohort.account_count} cuenta
                {cohort.account_count !== 1 ? 's' : ''}
              </p>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {selectedId === NEW_COHORT && (
          <AccountCohortForm
            key={NEW_COHORT}
            onSaved={(created) => setSelectedId(created.id)}
            onArchived={() => setSelectedId(null)}
          />
        )}
        {selected && (
          <AccountCohortForm
            key={selected.id}
            cohort={selected}
            onSaved={() => undefined}
            onArchived={() => setSelectedId(null)}
          />
        )}
        {!selectedId && (
          <p className="py-12 text-sm text-center text-gray-400">
            Elige un cohort de cuentas o crea uno nuevo.
          </p>
        )}
      </div>
    </div>
  );
};

export default AccountCohortsPanel;
